// The internal End-Use Statement editor and review gate.
//
// THE CENTRAL UI IDEA: a prefilled value looks DIFFERENT from an asserted one.
// A suggestion carries a visible "Suggested — confirm" affordance and a
// consequential suggestion is marked as blocking. An operator should never be
// able to tab through a form and accidentally certify something the software
// guessed, and they should be able to see at a glance which answers are still
// the software's rather than theirs.
//
// The gate shown here is ADVISORY. The server evaluates the same rules and
// refuses independently, so a stale page or a direct API call cannot get past
// it. This panel exists to stop the operator wasting a round trip, not to
// enforce anything.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import {
  AlertTriangle, Check, CircleAlert, FileDown, FileText, Lock, PenLine, Sparkles,
} from 'lucide-react';
import { ComplianceRequestPanel } from '@/components/compliance-request-panel';
import {
  acceptComplianceSubmission, generateComplianceDocument, getComplianceDocumentUrl,
  getComplianceSubmission, listComplianceTemplates, requestComplianceChanges,
  reviseComplianceSubmission, setComplianceClauses, setComplianceValues,
  submitComplianceSubmission,
  type ComplianceField, type ComplianceProvenance, 
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

const SUGGESTED: ComplianceProvenance[] = ['prefilled_unconfirmed', 'imported'];

const STATUS_PILL: Record<string, string> = {
  draft: 'pill-default',
  submitted: 'pill-info',
  under_review: 'pill-info',
  changes_requested: 'pill-warning',
  accepted: 'pill-success',
  superseded: 'pill-default',
  void: 'pill-default',
};

/** Only these fields are visible given the answers so far. Mirrors the server. */
function visible(field: ComplianceField, values: Record<string, string>): boolean {
  if (!field.visibleWhen) return true;
  const actual = values[field.visibleWhen.field];
  return actual !== undefined && field.visibleWhen.equals.includes(actual);
}

export function EusEditor({
  submissionPublicId, onClose,
}: {
  submissionPublicId: string;
  onClose: () => void;
}) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['compliance-submission', submissionPublicId],
    queryFn: () => getComplianceSubmission(submissionPublicId, () => getToken()),
  });

  // Cached by the Compliance page that opened this editor, so this is a cache
  // read rather than a second round trip in the common case.
  const { data: templates } = useQuery({
    queryKey: ['compliance-templates'],
    queryFn: () => listComplianceTemplates(() => getToken()),
  });

  // Local edits, keyed by field. Only fields the operator touched are sent.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [reviewNote, setReviewNote] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => { setDraft({}); setConfirmed(new Set()); }, [submissionPublicId]);

  const stored = useMemo(() => {
    const out: Record<string, { value: string; provenance: ComplianceProvenance }> = {};
    for (const v of data?.values ?? []) {
      out[v.fieldKey] = { value: v.value ?? '', provenance: v.provenance };
    }
    return out;
  }, [data]);

  /** Stored values overlaid with local edits — what the form currently shows. */
  const effective = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(stored)) out[k] = v.value;
    for (const [k, v] of Object.entries(draft)) out[k] = v;
    return out;
  }, [stored, draft]);

  const isDraft = data?.submission.status === 'draft';
  const frozen = data ? !isDraft : true;

  const saveValues = useMutation({
    mutationFn: async () => {
      const payload = Object.entries(draft).map(([fieldKey, value]) => ({
        fieldKey,
        value: value === '' ? null : value,
        provenance: 'entered_by_apd' as const,
      }));
      for (const fieldKey of confirmed) {
        if (draft[fieldKey] !== undefined) continue; // already sent as an edit
        payload.push({
          fieldKey,
          value: stored[fieldKey]?.value ?? null,
          provenance: 'prefilled_confirmed' as never,
        });
      }
      if (payload.length === 0) return null;
      return setComplianceValues(submissionPublicId, payload, () => getToken());
    },
    onSuccess: () => {
      setDraft({});
      setConfirmed(new Set());
      qc.invalidateQueries({ queryKey: ['compliance-submission', submissionPublicId] });
    },
    onError: (e) => setLocalError((e as { message?: string })?.message ?? 'Save failed'),
  });

  const answerClauses = useMutation({
    mutationFn: (refs: string[]) => setComplianceClauses(
      submissionPublicId, refs.map((r) => ({ clauseRef: r, answer: 'acknowledged' })), () => getToken(),
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance-submission', submissionPublicId] }),
    onError: (e) => setLocalError((e as { message?: string })?.message ?? 'Could not record the certifications'),
  });

  const submit = useMutation({
    mutationFn: () => submitComplianceSubmission(submissionPublicId, () => getToken()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-submission', submissionPublicId] });
      qc.invalidateQueries({ queryKey: ['compliance-submissions'] });
    },
    onError: (e) => setLocalError((e as { message?: string })?.message ?? 'Could not submit'),
  });

  const accept = useMutation({
    mutationFn: () => acceptComplianceSubmission(submissionPublicId, reviewNote || null, () => getToken()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-submission', submissionPublicId] });
      qc.invalidateQueries({ queryKey: ['compliance-submissions'] });
    },
    onError: (e) => setLocalError((e as { message?: string })?.message ?? 'Could not accept'),
  });

  const requestChanges = useMutation({
    mutationFn: () => requestComplianceChanges(submissionPublicId, reviewNote, () => getToken()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance-submission', submissionPublicId] }),
    onError: (e) => setLocalError((e as { message?: string })?.message ?? 'Could not request changes'),
  });

  const makeDocument = useMutation({
    mutationFn: () => generateComplianceDocument(submissionPublicId, () => getToken()),
    onSuccess: () => { setLocalError(null); void refetch(); },
    onError: (e) => setLocalError((e as { message?: string })?.message ?? 'Could not generate'),
  });

  const openDocument = useMutation({
    // A SHORT-LIVED signed URL, fetched at the moment of the click. Never held
    // in state: a stale one is a 403 and a cached one is a link that outlives
    // its window.
    mutationFn: () => getComplianceDocumentUrl(submissionPublicId, () => getToken()),
    onSuccess: (r) => {
      setLocalError(null);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    },
    onError: (e) => setLocalError((e as { message?: string })?.message ?? 'Could not open'),
  });

  const revise = useMutation({
    mutationFn: () => reviseComplianceSubmission(submissionPublicId, () => getToken()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-submissions'] });
      onClose();
    },
    onError: (e) => setLocalError((e as { message?: string })?.message ?? 'Could not create a revision'),
  });

  const dirty = Object.keys(draft).length > 0 || confirmed.size > 0;
  const errMsg = localError;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      {...dismiss}
    >
      <div className="bg-background rounded-lg shadow-xl w-full max-w-4xl max-h-[90dvh] flex flex-col">
        <div className="flex-shrink-0 px-5 py-3 border-b border-border flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-foreground-muted" aria-hidden />
              <h2 className="text-sm font-semibold truncate">
                {data?.template.name ?? 'End-Use Statement'}
              </h2>
              {data && (
                <span className={`pill ${STATUS_PILL[data.submission.status] ?? 'pill-default'}`}>
                  {data.submission.status.replace(/_/g, ' ')}
                </span>
              )}
              {data && data.submission.revision > 1 && (
                <span className="pill pill-default">rev {data.submission.revision}</span>
              )}
            </div>
            {data && (
              <p className="text-[11px] text-foreground-muted truncate mt-0.5">
                {data.partner.legalName} · {data.template.code} {data.template.versionLabel}
              </p>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost px-2" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          {isLoading && <p className="text-xs text-foreground-muted">Loading…</p>}

          {data && frozen && (
            <div className="card p-3 bg-surface-muted flex items-start gap-2">
              <Lock className="w-4 h-4 text-foreground-muted mt-0.5 flex-shrink-0" aria-hidden />
              <p className="text-[11px] leading-relaxed">
                <span className="font-medium">This statement is a snapshot and cannot be edited.</span>{' '}
                It records what was declared when it was submitted. To correct it, create a
                revision — the original is kept and marked superseded.
              </p>
            </div>
          )}

          {/* The gate. Advisory here; the server enforces the same rules. */}
          {data && data.gate.blockers.length > 0 && (
            <div className="card p-3 border-warning">
              <div className="flex items-center gap-2 mb-2">
                <CircleAlert className="w-4 h-4 text-warning" aria-hidden />
                <h3 className="text-xs font-semibold">
                  {data.gate.blockers.length} thing{data.gate.blockers.length === 1 ? '' : 's'} to
                  resolve before this can be submitted
                </h3>
              </div>
              <ul className="space-y-1.5">
                {data.gate.blockers.map((b) => (
                  <li key={`${b.kind}:${b.target}`} className="text-[11px] flex items-start gap-2">
                    <span className={`pill ${b.kind === 'clause_contradicted' ? 'pill-danger' : 'pill-warning'} flex-shrink-0`}>
                      {b.kind === 'consequential_unconfirmed' ? 'confirm'
                        : b.kind === 'clause_contradicted' ? 'conflict'
                        : b.kind === 'clause_unanswered' ? 'certify' : 'required'}
                    </span>
                    <span>{b.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Asking the customer, and following what happens next. */}
          {data && (
            <ComplianceRequestPanel
              submissionPublicId={submissionPublicId}
              submissionStatus={data.submission.status}
              request={data.request}
              customerWorkflowEnabled={templates?.customerWorkflowEnabled ?? false}
              onChanged={() => { void refetch(); }}
            />
          )}

          {/* The attestation. What a reviewer actually checks. */}
          {data?.signature && (
            <div className="card p-3">
              <div className="flex items-center gap-2 mb-2">
                <PenLine className="w-4 h-4 text-foreground-muted" aria-hidden />
                <h3 className="text-xs font-semibold">Signed</h3>
                <span className={`pill ${data.signature.signerKind === 'customer' ? 'pill-info' : 'pill-default'}`}>
                  by the {data.signature.signerKind === 'customer' ? 'customer' : 'team'}
                </span>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                <dt className="text-foreground-muted">Name</dt>
                <dd className="font-medium">{data.signature.signerName}</dd>
                {data.signature.signerTitle && (<>
                  <dt className="text-foreground-muted">Title</dt>
                  <dd>{data.signature.signerTitle}</dd>
                </>)}
                {data.signature.signerEmail && (<>
                  <dt className="text-foreground-muted">Email</dt>
                  <dd className="break-all">{data.signature.signerEmail}</dd>
                </>)}
                <dt className="text-foreground-muted">Signed at</dt>
                <dd>{new Date(data.signature.signedAt).toLocaleString()}</dd>
                <dt className="text-foreground-muted">Method</dt>
                <dd>{data.signature.method.replace(/_/g, ' ')}</dd>
                {data.signature.signedIp && (<>
                  <dt className="text-foreground-muted">From IP</dt>
                  <dd className="tabular-nums">{data.signature.signedIp}</dd>
                </>)}
              </dl>

              <details className="mt-2">
                <summary className="text-[11px] cursor-pointer text-foreground-muted">
                  The exact words they agreed to
                </summary>
                {/* Copied verbatim at signing. Not read back from the template,
                    so a later template edit cannot rewrite what was signed. */}
                <p className="text-[11px] whitespace-pre-line mt-1.5 p-2 bg-surface-muted rounded">
                  {data.signature.declarationText}
                </p>
              </details>

              {data.signature.contentSha256 && (
                <p className="text-[11px] text-foreground-muted mt-2 break-all">
                  Content digest <code>{data.signature.contentSha256}</code> — covers the values,
                  the certifications and the declaration as signed.
                </p>
              )}
            </div>
          )}

          {/* The retained document. */}
          {data && (data.document || data.submission.status === 'accepted') && (
            <div className="card p-3">
              <div className="flex items-center gap-2 mb-2">
                <FileDown className="w-4 h-4 text-foreground-muted" aria-hidden />
                <h3 className="text-xs font-semibold">Retained document</h3>
              </div>
              {data.document ? (
                <>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                    <dt className="text-foreground-muted">File</dt>
                    <dd className="break-all">{data.document.filename}</dd>
                    <dt className="text-foreground-muted">Generated</dt>
                    <dd>{new Date(data.document.generatedAt).toLocaleString()}</dd>
                    <dt className="text-foreground-muted">Size</dt>
                    <dd className="tabular-nums">{(data.document.byteSize / 1024).toFixed(1)} KB</dd>
                    <dt className="text-foreground-muted">Renderer</dt>
                    <dd>{data.document.renderer}</dd>
                  </dl>
                  <p className="text-[11px] text-foreground-muted mt-1 break-all">
                    SHA-256 <code>{data.document.sha256}</code>
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button
                      onClick={() => openDocument.mutate()}
                      disabled={openDocument.isPending}
                      className="btn-outline text-xs"
                    >
                      <FileDown className="w-3.5 h-3.5" aria-hidden />
                      {openDocument.isPending ? 'Preparing…' : 'View / download'}
                    </button>
                  </div>
                  <p className="text-[11px] text-foreground-muted mt-2">
                    This is the document as it was produced when the statement was accepted.
                    Downloading it returns those exact bytes &mdash; it is never re-rendered.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-[11px] text-foreground-muted">
                    No document has been retained for this statement yet.
                  </p>
                  <button
                    onClick={() => makeDocument.mutate()}
                    disabled={makeDocument.isPending}
                    className="btn-outline text-xs mt-2"
                  >
                    {makeDocument.isPending ? 'Generating…' : 'Generate document'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Units this statement covers, and any whose serial is unknown. */}
          {data && data.units.length > 0 && (
            <div className="card p-3">
              <h3 className="text-xs font-semibold mb-1.5">Serialized units covered</h3>
              <ul className="text-[11px] space-y-1">
                {data.units.map((u) => (
                  <li key={u.publicId} className="flex items-center gap-2">
                    <span className="font-medium">{u.partNumber ?? 'Part'}</span>
                    {u.serialNumber ? (
                      <span className="tabular-nums">{u.serialNumber}</span>
                    ) : (
                      <span className="pill pill-warning">serial not recorded</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Where the assertion disagrees with the live company record. */}
          {data && data.canonicalDiff.length > 0 && (
            <div className="card p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-foreground-muted" aria-hidden />
                <h3 className="text-xs font-semibold">
                  Differs from the current company record
                </h3>
              </div>
              <p className="text-[11px] text-foreground-muted mb-2">
                Not necessarily wrong — the customer may have moved, or may be correcting
                something we had wrong. Nothing is changed automatically in either direction.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-[480px] w-full text-[11px]">
                  <thead>
                    <tr className="text-foreground-muted text-left">
                      <th className="py-1 pr-3 font-medium">Field</th>
                      <th className="py-1 pr-3 font-medium">Company record</th>
                      <th className="py-1 pr-3 font-medium">This statement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.canonicalDiff.map((d) => (
                      <tr key={d.fieldKey} className="border-t border-border">
                        <td className="py-1.5 pr-3">{d.label}</td>
                        <td className="py-1.5 pr-3 text-foreground-muted">{d.canonicalValue ?? '—'}</td>
                        <td className="py-1.5 pr-3 font-medium">{d.submittedValue ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* The form. */}
          {data?.schema.sections.map((section) => (
            <div key={section.key} className="card p-4">
              <h3 className="text-sm font-semibold">{section.title}</h3>
              {section.description && (
                <p className="text-[11px] text-foreground-muted mt-0.5 mb-3">{section.description}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                {section.fields.filter((f) => visible(f, effective)).map((field) => {
                  const entry = stored[field.key];
                  const isSuggestion = entry ? SUGGESTED.includes(entry.provenance) : false;
                  const locallyConfirmed = confirmed.has(field.key);
                  const edited = draft[field.key] !== undefined;
                  const value = effective[field.key] ?? '';

                  return (
                    <label key={field.key} className={field.type === 'long_text' ? 'sm:col-span-2 block' : 'block'}>
                      <span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground-muted mb-1">
                        {field.label}
                        {field.required && <span aria-hidden>*</span>}
                        {field.consequential && (
                          <span className="pill pill-warning" title="A person must confirm this answer">
                            must confirm
                          </span>
                        )}
                      </span>

                      {field.type === 'select' ? (
                        <select
                          value={value}
                          disabled={frozen}
                          onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm bg-surface border border-border rounded disabled:opacity-60"
                        >
                          <option value="">—</option>
                          {field.options?.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : field.type === 'long_text' ? (
                        <textarea
                          value={value}
                          disabled={frozen}
                          rows={3}
                          maxLength={field.maxLength}
                          onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm bg-surface border border-border rounded disabled:opacity-60"
                        />
                      ) : (
                        <input
                          type="text"
                          value={value}
                          disabled={frozen}
                          maxLength={field.maxLength}
                          onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm bg-surface border border-border rounded disabled:opacity-60"
                        />
                      )}

                      {/* A suggestion is visibly NOT an answer until confirmed. */}
                      {!frozen && isSuggestion && !edited && (
                        <span className="flex items-center gap-2 mt-1">
                          <span className="inline-flex items-center gap-1 text-[11px] text-foreground-muted">
                            <Sparkles className="w-3 h-3" aria-hidden />
                            {entry?.provenance === 'imported' ? 'Carried from the previous revision' : 'Suggested'}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const next = new Set(confirmed);
                              if (locallyConfirmed) next.delete(field.key); else next.add(field.key);
                              setConfirmed(next);
                            }}
                            className={locallyConfirmed ? 'pill pill-success' : 'btn-ghost text-[11px] px-1.5 py-0.5'}
                          >
                            {locallyConfirmed ? <><Check className="w-3 h-3" aria-hidden /> Confirmed</> : 'Confirm'}
                          </button>
                        </span>
                      )}
                      {field.helpText && (
                        <span className="block text-[11px] text-foreground-muted mt-1">{field.helpText}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Certifications, with their exact wording. */}
          {data && data.clauses.length > 0 && (
            <div className="card p-4">
              <h3 className="text-sm font-semibold mb-1">Certifications</h3>
              <p className="text-[11px] text-foreground-muted mb-3">
                The exact wording is recorded with the answer, so this statement stays readable
                as it was even if the template is revised later.
              </p>
              <ul className="space-y-2">
                {data.clauses.map((c) => {
                  const answered = data.clauseAnswers.find((a) => a.clauseRef === c.clauseRef);
                  return (
                    <li key={c.clauseRef} className="text-[11px] flex items-start gap-2">
                      <span className={`pill ${answered ? 'pill-success' : 'pill-default'} flex-shrink-0`}>
                        {c.clauseRef}
                      </span>
                      <span className="leading-relaxed">{c.exactText}</span>
                    </li>
                  );
                })}
              </ul>
              {!frozen && data.clauseAnswers.length < data.clauses.length && (
                <button
                  type="button"
                  onClick={() => answerClauses.mutate(data.clauses.map((c) => c.clauseRef))}
                  disabled={answerClauses.isPending}
                  className="btn-outline text-xs mt-3"
                >
                  {answerClauses.isPending ? 'Recording…' : 'Acknowledge all certifications'}
                </button>
              )}
            </div>
          )}

          {data?.submission.reviewNotes && (
            <div className="card p-3">
              <h3 className="text-xs font-semibold mb-1">Reviewer notes</h3>
              <p className="text-[11px] whitespace-pre-wrap">{data.submission.reviewNotes}</p>
            </div>
          )}

          {errMsg && (
            <p className="text-xs text-destructive flex items-start gap-1.5">
              <CircleAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" aria-hidden />
              {errMsg}
            </p>
          )}
        </div>

        <div className="flex-shrink-0 px-5 py-3 border-t border-border flex items-center justify-between gap-3">
          <span className="text-[11px] text-foreground-muted">
            {dirty ? `${Object.keys(draft).length + confirmed.size} unsaved change(s)` : 'No unsaved changes'}
          </span>
          <div className="flex items-center gap-2">
            {isDraft && (
              <>
                <button
                  onClick={() => saveValues.mutate()}
                  disabled={!dirty || saveValues.isPending}
                  className="btn-outline text-xs"
                >
                  {saveValues.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => submit.mutate()}
                  disabled={dirty || submit.isPending || !data?.gate.canSubmit}
                  title={dirty ? 'Save your changes first' : undefined}
                  className="btn-primary text-xs"
                >
                  {submit.isPending ? 'Submitting…' : 'Submit for review'}
                </button>
              </>
            )}
            {data && ['submitted', 'under_review'].includes(data.submission.status) && (
              <>
                <input
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder="Reviewer note"
                  className="px-2 py-1 text-xs bg-surface border border-border rounded w-48"
                />
                <button
                  onClick={() => requestChanges.mutate()}
                  disabled={!reviewNote.trim() || requestChanges.isPending}
                  className="btn-outline text-xs"
                >
                  Request changes
                </button>
                <button
                  onClick={() => accept.mutate()}
                  disabled={accept.isPending || !data.gate.canSubmit}
                  className="btn-primary text-xs"
                >
                  {accept.isPending ? 'Accepting…' : 'Accept'}
                </button>
              </>
            )}
            {data && ['accepted', 'changes_requested'].includes(data.submission.status) && (
              <button
                onClick={() => revise.mutate()}
                disabled={revise.isPending}
                className="btn-outline text-xs"
              >
                {revise.isPending ? 'Creating…' : 'Create revision'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
